import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import './ApplicantDetail.css';
import Dashboard from './Dashboard.js';
import ProfileDetails from "../components/ProfileDetails.js";
import InterviewSchedule from "../components/InterviewSchedule.js";
import ResumeDetails from "../components/ResumeDetails.js";
import ProcessDetail from "../components/ProcessDetail.js";
import { useCompany } from '../context/CompanyContext';

const ApplicantDetail = () => {
  const { selectedCompany } = useCompany();
  
  const [applicant, setApplicant] = useState({
    'user_names': "",
    'job_title': "",
    'status': "",
    'stages': "",
    'user_phone_number': "",
    'user_email': "",
    'languages': [],
    'addresses': [],
    'bio': "",
    'skills': [],
    'process_update': [],
    'user_id': ""
  });
  const [loading, setLoading] = useState(false);
  const [loadingNotes, setLoadingNote] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  // const [companyId, setCompanyId] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [notes, setNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveData, setMoveData] = useState({
    title: '',
    details: '',
    deadline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { applicantId } = useParams();
  const navigate = useNavigate();

  const formatDateOrDaysAgo = (date) => {
    if (!date) return '';
    
    // Convert string to Date object if needed
    const targetDate = new Date(date);
    const today = new Date();
    
    // Check if date is valid
    if (isNaN(targetDate.getTime())) {
      return '';
    }
    
    // Reset time to start of day for accurate day calculation
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const targetStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    // Calculate difference in milliseconds, then convert to days
    const diffInMs = todayStart - targetStart;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    // If more than 30 days ago, return formatted date
    if (diffInDays > 30) {
      return targetDate.toISOString().split('T')[0]; // Y-M-D format
    }
    
    // Handle different cases
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return '1 day ago';
    } else if (diffInDays > 1) {
      return `${diffInDays} days ago`;
    } else if (diffInDays === -1) {
      return 'Tomorrow';
    } else {
      return `In ${Math.abs(diffInDays)} days`;
    }
  };

  const getAvailableStatusOptions = (currentStatus) => {
    const statusMap = {
      'pending': ['active', 'rejected', 'cancelled'],
      'active': ['offered', 'rejected', 'cancelled'],
      'offered': ['rejected', 'cancelled'],
      'rejected': [],
      'cancelled': []
    };
    return statusMap[currentStatus] || [];
  };

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication token not found');
        setIsUpdatingStatus(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-applications/applicants/${applicantId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Status updated successfully:', result);
        
        // Update local state
        fetchApplicantDetail(selectedCompany.company_id);
        setShowStatusDropdown(false);
                
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleMoveToNextStep = async () => {
    if (!moveData.title.trim() || !moveData.details.trim()) {
      alert('Please fill in both title and details');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication token not found');
        setIsSubmitting(false);
        return;
      }
      const index = progress_stages.indexOf(applicant.stages)

      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-applications/add-process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          application_id: applicant?.application_id || 1, // Replace with actual applicant ID
          title: moveData.title,
          details: moveData.details,
          stages: progress_stages[index+1],
          deadline_date: moveData.deadline,
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Stage moved successfully:', result);
        
        // Close modal and reset form
        setShowMoveModal(false);
        setMoveData({ title: '', details: '', deadline: '' });
        
        fetchApplicantDetail(selectedCompany.company_id);
      } else {
        const errorData = await response.json();
        setError(errorData.details || 'Failed to move to next stage');
      }
    } catch (error) {
      console.error('Error moving to next stage:', error);
      setError('Failed to move to next stage. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowMoveModal(false);
    setMoveData({ title: '', details: '' });
  };

  const fetchApplicantDetail = async (companyId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Uncomment this when your API is ready
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/job-applications/get-application/${companyId}/${applicantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplicant(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch applicant details');
      }
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch applicant details');
      setLoading(false);
      console.error('Error fetching applicant details:', error);
    }
  };

  useEffect(() => {
    if (applicantId && selectedCompany) {
      fetchApplicantDetail(selectedCompany.company_id);
      fetchNotes(selectedCompany.company_id);
    }
  }, [applicantId, selectedCompany]);

  if (loading || loadingNotes) {
    return <div className="loading-state">Loading applicant details...</div>;
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  if (!applicant) {
    return <div className="error-state">Applicant not found</div>;
  }

  const progress_stages = ['submitted', 'test', 'interview', 'decision' ];

  const handleAddNote = async ()  => {
    if (newNote.trim() && selectedCompany) {
      setLoadingNote(true);
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('Authentication token not found');
          setLoadingNote(false);
          return;
        }
        const response = await fetch(`${process.env.REACT_APP_API_URL}/notes/create/${selectedCompany.company_id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            application_id: applicant?.application_id || 1, // Replace with actual applicant ID
            notes: newNote,
          })
        });
        if (response.ok) {
          const data = await response.json();
          setNewNote('');
          fetchNotes(selectedCompany.company_id);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to fetch applicant details');
        }
        setLoadingNote(false);
      } catch (error) {
        setError('Failed to fetch applicant details');
        setLoadingNote(false);
        console.error('Error fetching applicant details:', error);
      } finally {
        setShowAddNote(false);
      }
    }
  };


  const fetchNotes = async (companyId) => {
    setLoadingNote(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoadingNote(false);
        return;
      }      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notes/list-notes/${companyId}/${applicantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotes(data.items);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch applicant details');
      }
      setLoadingNote(false);
    } catch (error) {
      setError('Failed to fetch applicant details');
      setLoadingNote(false);
      console.error('Error fetching applicant details:', error);
    }
  };

  const formatStatusDisplay = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <Dashboard page={"application"} >

      <div className="applicant-detail-content">
        <div className="detail-header">
          <button className="back-btn" onClick={() => {navigate('/applications')}}>
            ← 
          </button>
          <h1>Applicant Details</h1>
          <div className="header-actions">
            <div className="status-dropdown-container">
              <button 
                className="more-action-btn"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'More Action'} ▼
              </button>
              
              {showStatusDropdown && (
                <div className="status-dropdown">
                  <div className="dropdown-header">
                    <span>Current Status: {formatStatusDisplay(applicant?.status || 'pending')}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-options">
                    {getAvailableStatusOptions(applicant?.status || 'pending').length > 0 ? (
                      getAvailableStatusOptions(applicant?.status || 'pending').map(status => (
                        <button
                          key={status}
                          className="dropdown-option"
                          onClick={() => handleStatusUpdate(status)}
                          disabled={isUpdatingStatus}
                        >
                          Change to {formatStatusDisplay(status)}
                        </button>
                      ))
                    ) : (
                      <div className="no-options">
                        No status changes available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-body">
          <div className="applicant-summary">
            <div className="applicant-avatar">
              <img src={`${process.env.REACT_APP_API_URL}/profile/get-pic/${encodeURIComponent(applicant.user_id)}`} alt={applicant.user_names} />
            </div>
            <div className="applicant-basic-info">
              <h2>{applicant.user_names}</h2>
              <p className="role">{applicant.job_title}</p>
              <div className="score">
                <span className="star">⭐</span>
                {0}
              </div>
            </div>
            <div className="application-info">
              <div className="applied-info">
                <span className="label">Applied Jobs</span>
                <span className="days-ago">{formatDateOrDaysAgo(applicant.created_at || '')}</span>
              </div>
              <h3>{applicant.job_title}</h3>
              <p>{applicant.job_type}</p>
            </div>
          </div>

          <div className="stage-progress">
            <div className="stage-header">
              <span className="stage-label">Stage</span>
              <span className="stage-name">{applicant.status}</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${applicant.stages}%` }}
              ></div>
            </div>
            <button className="schedule-interview-btn">
              📅 Schedule Interview
            </button>
          </div>

          <div className="contact-section">
            <h3>Contact</h3>
            <div className="contact-item">
              <span className="contact-icon">📧</span>
              <div>
                <span className="contact-label">Email</span>
                <span className="contact-value">{applicant.user_email}</span>
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📱</span>
              <div>
                <span className="contact-label">Phone</span>
                <span className="contact-value">{applicant.user_phone_number}</span>
              </div>
            </div>
            {/* <div className="contact-item">
              <span className="contact-icon">📷</span>
              <div>
                <span className="contact-label">Instagram</span>
                <a href={`https://${applicant.contact.instagram}`} className="contact-link">
                  {applicant.contact.instagram}
                </a>
              </div>
            </div> */}
            {/* <div className="contact-item">
              <span className="contact-icon">🐦</span>
              <div>
                <span className="contact-label">Twitter</span>
                <a href={`https://${applicant.contact.twitter}`} className="contact-link">
                  {applicant.contact.twitter}
                </a>
              </div>
            </div> */}
            {/* <div className="contact-item">
              <span className="contact-icon">🌐</span>
              <div>
                <span className="contact-label">Website</span>
                <a href={`https://${applicant.contact.website}`} className="contact-link">
                  {applicant.contact.website}
                </a>
              </div>
            </div> */}
          </div>
        </div>

        <div className="detail-tabs-section">
          <div className="tabs-header">
            <button 
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Applicant Profile
            </button>
            <button 
              className={`tab ${activeTab === 'resume' ? 'active' : ''}`}
              onClick={() => setActiveTab('resume')}
            >
              Resume
            </button>
            <button 
              className={`tab ${activeTab === 'hiring' ? 'active' : ''}`}
              onClick={() => setActiveTab('hiring')}
            >
              Hiring Progress
            </button>
            <button 
              className={`tab ${activeTab === 'interview' ? 'active' : ''}`}
              onClick={() => setActiveTab('interview')}
            >
              Interview Schedule
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'profile' && (
              <ProfileDetails applicant={applicant} />
            )}

            {activeTab === 'hiring' && (
              <>
                <ProcessDetail applicant={applicant} 
                                    notes={notes} 
                                    newNote={newNote}
                                    setNewNote={setNewNote}
                                    handleAddNote={handleAddNote} 
                                    handleMoveNextClicked={() => {setShowMoveModal(true)}}
                                    />
                  {showMoveModal && (
                    
                      <div className="modal-overlay" onClick={handleCloseModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                          <div className="modal-header">
                            <h3>Move to Next Step</h3>
                            <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
                          </div>
                          
                          <div className="modal-body">
                            <div className="form-group">
                              <label htmlFor="move-title">Title</label>
                              <input
                                id="move-title"
                                type="text"
                                value={moveData.title}
                                onChange={(e) => setMoveData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Enter title for this stage transition"
                                className="form-input"
                              />
                            </div>
                            
                            <div className="form-group">
                              <label htmlFor="move-details">Details</label>
                              <textarea
                                id="move-details"
                                value={moveData.details}
                                onChange={(e) => setMoveData(prev => ({ ...prev, details: e.target.value }))}
                                placeholder="Enter details about this stage transition..."
                                className="form-textarea"
                                rows="4"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="move-deadline">Deadline Date</label>
                              <input
                                id="move-deadline"
                                type="date"
                                value={moveData.deadline}
                                onChange={(e) => setMoveData(prev => ({ ...prev, deadline: e.target.value }))}
                                className="form-input"
                                min={new Date().toISOString().split('T')[0]} // Prevent past dates
                              />
                            </div>
                          </div>
                          
                          <div className="modal-footer">
                            <button 
                              onClick={handleCloseModal} 
                              className="cancel-btn"
                              disabled={isSubmitting}
                            >
                              Cancel
                            </button>
                            <button 
                              onClick={handleMoveToNextStep} 
                              className="submit-btn"
                              disabled={isSubmitting || !moveData.title.trim() || !moveData.details.trim() || !moveData.deadline}
                              >
                              {isSubmitting ? 'Moving...' : 'Move to Next Step'}
                            </button>
                          </div>
                        </div>
                      </div>
                    
                  )}
              </>
            )}

            {activeTab === 'resume' && (
              <ResumeDetails applicant={applicant} />
            )}
            {activeTab === 'interview' && (
              <InterviewSchedule applicant={applicant} />
            )}
            {showStatusDropdown && (
                <div 
                  className="dropdown-backdrop" 
                  onClick={() => setShowStatusDropdown(false)}
                ></div>
              )}
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default ApplicantDetail;